import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { plansService } from '@/services/plansService';
import type { CreatePlanDto, UpdatePlanDto, PlanSyncMapping, CreateStripeProductDto } from '@/types/admin.types';

export function usePlans() {
  return useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: plansService.getAll,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function usePlan(id: string) {
  return useQuery({
    queryKey: ['admin', 'plans', id],
    queryFn: () => plansService.getById(id),
    enabled: !!id,
    staleTime: 1000 * 60, // 1 minute
  });
}

export function useCreatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreatePlanDto) => plansService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
    },
  });
}

export function useUpdatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdatePlanDto }) =>
      plansService.update(id, dto),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans', variables.id] });
    },
  });
}

export function useDeletePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => plansService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
    },
  });
}

// Stripe sync hooks
export function useStripeProducts() {
  return useQuery({
    queryKey: ['admin', 'plans', 'stripe', 'products'],
    queryFn: plansService.getStripeProducts,
    staleTime: 1000 * 60, // 1 minute
  });
}

export function useSyncStripeProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mapping: PlanSyncMapping) => plansService.syncStripeProduct(mapping),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
    },
  });
}

export function useSyncAllStripeProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mappings: PlanSyncMapping[]) => plansService.syncAllStripeProducts(mappings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
    },
  });
}

export function useCreateStripeProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateStripeProductDto) => plansService.createStripeProduct(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans', 'stripe', 'products'] });
    },
  });
}

